<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useSettingsStore, useUserStore } from '../stores';
import type { PersonalCenterList } from './Public/type';
import BaseContent from './Public/BaseContent.vue';
import { getPersonalCenterOptions } from './Public/getPersonalCenterOptions';
import { type PersonalCenterOption } from '../stores/modules/settings/types';
import IconDark from './Icon/IconDark.vue';
import IconLight from './Icon/IconLight.vue';
import { useRoute } from 'vue-router';
import { onSensorsWebClick } from '../plugins/sensors';

const route = useRoute();

const props = defineProps<{
  config: PersonalCenterList[number];
}>();

const themeExtraContent = computed(() => {
  const config = props.config.props?.extraContent?.find((item: PersonalCenterList[number]) => item.id === 'theme');
  let options = [] as { label: string; value: string; iconColor: Record<'default' | 'hover' | 'active', string> }[];
  try {
    options = JSON.parse(config?.optionsJson || '[]');
  } catch (error) {
    console.error(error);
  }
  return { config, options };
});

const configWithoutComp = computed(() => {
  const { comp, ...others } = props.config || {};
  return others;
});

const settingsStore = useSettingsStore();
const userStore = useUserStore();

const theme = computed(() => settingsStore.settings.theme);
const menu = computed(() => settingsStore.settings.menu);
const OPTIONS: Record<string, { className?: string; thumbnail?: string; value: string }[]> = {
  'blue-head': [
    {
      className: 'blue-light-head',
      value: 'light',
    },
    {
      className: 'blue-brand-head',
      value: 'brand',
    },
    {
      className: 'blue-black-head',
      value: 'black',
    },
  ],
  'blue-side': [
    {
      className: 'blue-light-side',
      value: 'light',
    },
    {
      className: 'blue-brand-side',
      value: 'brand',
    },
    {
      className: 'blue-black-side',
      value: 'black',
    },
  ],
  'blue-head-side': [
    {
      className: 'blue-light-head-side',
      value: 'light',
    },
    {
      className: 'blue-brand-head-side',
      value: 'brand',
    },
    {
      className: 'blue-black-head-side',
      value: 'black',
    },
    {
      className: 'blue-black-black-head-side',
      value: 'black-black',
    },
  ],
  'red-head': [
    {
      className: 'red-light-head',
      value: 'light',
    },
    {
      className: 'red-black-head',
      value: 'black',
    },
  ],
  'red-side': [
    {
      className: 'red-light-side',
      value: 'light',
    },
    {
      className: 'red-black-side',
      value: 'black',
    },
  ],
  'red-head-side': [
    {
      className: 'red-light-head-side',
      value: 'light',
    },
    {
      className: 'red-black-head-side',
      value: 'black',
    },
    {
      className: 'red-black-black-head-side',
      value: 'black-black',
    },
  ],
};
const options = computed(() => {
  return (
    props.config.layoutStyleExtendList?.reduce((obj, cur) => {
      const { theme, menuMode, thumbnail, value } = cur;
      const key = `${theme}-${menuMode}` as keyof typeof OPTIONS;
      if (!obj[key]) {
        obj[key] = [];
      }
      obj[key].push({ thumbnail, value });
      return obj;
    }, OPTIONS) || {}
  );
});
const list = ref<any[]>(options.value[`${theme.value.style}-${menu.value.menuMode}`]);
const placement = computed(
  () =>
    ({ 'outside': 'bottom-end', 'personal-center-dropdown': 'left-start', 'layout-style': 'bottom-end' })[
      props.config.placement || 'outside'
    ],
);

const updateUserConfig = async (key: string, value: any) => {
  if (!settingsStore.settings.system.openUserConfigCustomStorage) {
    return;
  }

  try {
    const config = { key, value };
    await settingsStore.settings.system.setUserConfig?.(config, userStore.originUserInfo);
    // ElMessage({ type: 'success', message: '保存成功' });
  } catch (error) {
    console.warn('风格设置保存失败', error);
    // ElMessage({ type: 'error', message: '保存失败，请稍后再试' });
  }
};
const handleClick = async (value: string) => {
  settingsStore.settings.theme.layoutStyle = value;
  await updateUserConfig('layoutStyle', value);
};

// 监听主题变化
watch(
  () => settingsStore.settings.theme.style,
  () => {
    if (settingsStore.settings.theme.style) {
      list.value = options.value[`${theme.value.style}-${menu.value.menuMode}`];
    }
  },
);

// 监听导航栏模式变化
watch(
  () => settingsStore.settings.menu.menuMode,
  () => {
    if (settingsStore.settings.menu.menuMode) {
      list.value = options.value[`${theme.value.style}-${menu.value.menuMode}`];
    }
  },
);

// 监听风格设置列表变化，判断上一次选择的风格是否存在，不存在则设置默认值
watch(list, () => {
  const isExist = list.value?.find((item) => item.value === theme.value.layoutStyle);
  if (!isExist) {
    settingsStore.settings.theme.layoutStyle = list.value?.[0]?.value;
  }
});

const title = computed(() => {
  return props.config?.label || '风格设置';
});

const THEME_MODE_TEXT_MAP = { 深色模式: '夜间', 浅色模式: '日间' };

/** 日间模式才显示导航栏样式 */
const showLayoutStyleSetting = computed(() => settingsStore.settings.theme.mode === 'light');
const themeModeTarget = computed(() => settingsStore.settings.personalCenter.find((item) => item.id === 'theme-mode'));
const showThemeModeSetting = ref(false);

// 支持某些菜单才显示模式选择
watch(
  () => route.fullPath,
  async () => {
    if (typeof themeModeTarget.value?.show === 'function') {
      showThemeModeSetting.value = themeModeTarget.value?.show();
    } else {
      showThemeModeSetting.value = !!themeModeTarget.value?.show;
    }
  },
  { immediate: true },
);

const tabs = ref<PersonalCenterOption[]>([]);
watch(
  () => settingsStore.settings.personalCenter,
  async () => {
    tabs.value = (await getPersonalCenterOptions(themeModeTarget.value)) || [];
  },
  { immediate: true },
);
/**
 * 上报事件
 * @param elementContent 上报内容
 */
 const uploadEvent = (elementContent: string) => {
  const eventData = {
    title: elementContent,
    source: `theme-mode`,
  };
  onSensorsWebClick(eventData);
};
/**
 * 透传
 */
const onTabChange = async (value: string | number) => {
  // @ts-ignore
  const v = await import('@pdfe/sf-dark');
  const item = settingsStore.settings.personalCenter.find((item) => item.id === 'theme-mode');
  const onThemeModeChange = item?.onChange;
  const label = tabs.value.find((item) => item.value === value)?.label || '';
  uploadEvent(label);
  if (onThemeModeChange) {
    onThemeModeChange(
      value,
      {
        title: '个人中心下拉菜单',
        source: 'personalCenter-dropdown',
      },
      item,
    );
  } else {
    if (value === 'dark') {
      // 开启夜间模式
      v.enableDark(); // enableDark([options]) // 可自定义配置
    } else {
      // 关闭夜间模式
      v.disableDark();
    }
  }
  await updateUserConfig('theme-mode', value);
};
</script>

<template>
  <div class="LayoutStyle">
    <el-popover
      popper-class="LayoutStyle-popover preventPersonalCenterDropdownHide"
      :placement="placement"
      :title="title"
      width="auto"
      trigger="click"
      :hide-after="0"
    >
      <div v-if="themeExtraContent.config?.show" :class="`LayoutStyle-content ${theme.style}`">
        <p class="LayoutStyle-title">{{ themeExtraContent.config.label }}</p>
        <div class="LayoutStyle-theme-box-container">
          <div
            v-for="item in themeExtraContent.options"
            :key="item.value"
            :class="{ active: item.value === theme.style }"
            :style="`
              --layout-style-theme-default: ${item.iconColor?.default || ''};
              --layout-style-theme-hover: ${item.iconColor?.hover || ''};
              --layout-style-theme-active: ${item.iconColor?.active || ''}
            `"
            @click="theme.style = item.value as typeof theme.style"
          />
        </div>
      </div>

      <div :class="`LayoutStyle-content ${theme.style}`">
        <template v-if="showThemeModeSetting">
          <p class="LayoutStyle-title">模式选择</p>
          <el-tabs
            v-model="settingsStore.settings.theme.mode"
            type="card"
            class="cachet LayoutStyle-tabs"
            @tab-change="onTabChange"
          >
            <el-tab-pane v-for="item in tabs" :key="item.label" :name="item.value as string">
              <template #label>
                <IconLight v-if="item.value === 'light'" class="mr-4" />
                <IconDark v-if="item.value === 'dark'" class="mr-4" />
                {{ THEME_MODE_TEXT_MAP[item.label as keyof typeof THEME_MODE_TEXT_MAP] || item.label }}
              </template>
            </el-tab-pane>
          </el-tabs>
        </template>

        <template v-if="showLayoutStyleSetting">
          <p class="LayoutStyle-title">导航栏样式</p>
          <div class="LayoutStyle-options">
            <div
              v-for="item in list"
              :key="`${item.label}/${item.value}`"
              :class="{
                [item.className]: true,
                card: true,
                active: item.value === theme.layoutStyle,
              }"
              :style="item.thumbnail ? { backgroundImage: `url(${item.thumbnail})` } : {}"
              @click="handleClick(item.value)"
            />
          </div>
        </template>
      </div>
      <template #reference>
        <div @click="(event) => event.stopPropagation()">
          <BaseContent :config="configWithoutComp" pure />
        </div>
      </template>
    </el-popover>
  </div>
</template>

<style lang="scss">
@import '../styles/var.scss';

.#{$namespace}-popover.#{$namespace}-popper.LayoutStyle-popover {
  padding: 16px;
  min-width: 420px !important;
}
</style>

<style lang="scss" scoped>
@import '../styles/var.scss';

.mr-4 {
  margin-right: 4px;
}

.LayoutStyle {
  &-content {
    padding: 16px;
    border-radius: 8px;
    &.blue {
      background: rgba(244, 245, 252, 0.88);
    }
    &.red {
      background: rgba(249, 246, 246, 0.88);
    }

    &:not(:first-child) {
      margin-top: 16px;
    }
  }
  &-title {
    font-size: 14px;
    color: #3a3d4f;
    margin-bottom: 8px;
  }
  &-theme-box-container {
    display: flex;
    align-items: center;

    & > * {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      background-color: var(--layout-style-theme-default);
      cursor: pointer;

      &:not(:first-child) {
        margin-left: 16px;
      }

      &:hover {
        background-color: var(--layout-style-theme-hover);
      }

      &:active {
        background-color: var(--layout-style-theme-active);
      }

      &.active {
        background-image: url(../assets/images/yes.svg);
        background-repeat: no-repeat;
        background-size: contain;
        background-position: center;
      }
    }
  }
  &-tabs {
    width: fit-content;
    margin-bottom: 24px;

    :deep(.#{$namespace}-tabs__header) {
      background-color: var(--sdesign-color-neutral-4);
      margin: 0;
      height: 40px;

      .#{$namespace}__item {
        height: 32px;
        line-height: 32px;
      }
    }
  }
  &-options {
    display: flex;
    margin: 0 -8px;
    .card {
      width: 108px;
      height: 110px;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      border-radius: 8px;
      margin: 0 8px;
      cursor: pointer;
      &:hover {
        box-shadow: 0 0 0 2px var(--sdesign-color-brand-5);
      }
      &:active {
        box-shadow: 0 0 0 2px var(--sdesign-color-brand-7);
      }
      &.active {
        box-shadow: 0 0 0 2px var(--sdesign-color-brand-6);
      }
    }
    .blue-black-head-side {
      background-image: url('../assets/images/layout-black-head-side.svg');
    }
    .blue-black-black-head-side {
      background-image: url('../assets/images/layout-black-black-head-side.svg');
    }
    .blue-brand-head-side {
      background-image: url('../assets/images/layout-brand-head-side.svg');
    }
    .blue-light-head-side {
      background-image: url('../assets/images/layout-light-head-side.svg');
    }
    .blue-black-head {
      background-image: url('../assets/images/layout-black-head.svg');
    }
    .blue-brand-head {
      background-image: url('../assets/images/layout-brand-head.svg');
    }
    .blue-light-head {
      background-image: url('../assets/images/layout-light-head.svg');
    }
    .blue-black-side {
      background-image: url('../assets/images/layout-black-side.svg');
    }
    .blue-brand-side {
      background-image: url('../assets/images/layout-brand-side.svg');
    }
    .blue-light-side {
      background-image: url('../assets/images/layout-light-side.svg');
    }
    .red-black-head-side {
      background-image: url('../assets/images/layout-red-black-head-side.svg');
    }
    .red-black-black-head-side {
      background-image: url('../assets/images/layout-red-black-black-head-side.svg');
    }
    .red-light-head-side {
      background-image: url('../assets/images/layout-red-light-head-side.svg');
    }
    .red-black-head {
      background-image: url('../assets/images/layout-red-black-head.svg');
    }
    .red-light-head {
      background-image: url('../assets/images/layout-red-light-head.svg');
    }
    .red-black-side {
      background-image: url('../assets/images/layout-red-black-side.svg');
    }
    .red-light-side {
      background-image: url('../assets/images/layout-red-light-side.svg');
    }
  }
}
</style>
